import { type ReactNode, useEffect } from "react";
import { PageLoaderProvider, usePageLoader } from "@/contexts/page-loader-context";
import PageLoader from "@/components/ui/page-loader";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ToastProvider } from "@/contexts/toast-context";
import { useAuth } from "@/modules/auth";
import { useBrands, usePlan } from "@/modules/brand";

declare global {
	interface Window {
		Featurebase: (command: string, options?: Record<string, unknown>) => void;
	}
}

function PageLoaderOverlay() {
	const { isLoading } = usePageLoader();

	if (!isLoading) return null;

	return <PageLoader />;
}

function FeaturebaseWidget() {
	const { user, currentWorkspaceId } = useAuth();
	const { brands } = useBrands();

	useEffect(() => {
		(async () => {
			if (brands) {
				if (user?.email && window.Featurebase) {
					window.Featurebase("boot", {
						organization: "sayaboutus",
						appId: "69495a85133f1358a222266b",
						theme: "light",
						language: "en",
						email: user.email,
						name: [user.first_name, user.last_name].filter(Boolean).join(" ") || undefined,
						companies: [{ id: currentWorkspaceId, name: brands.find((brand) => brand.id === currentWorkspaceId)?.name || "" }],
						id: user.id,
					});
				}
			}
		})();
	}, [user, currentWorkspaceId, brands]);

	return null;
}

function PlanLoader() {
	const { currentWorkspaceId } = useAuth();
	const { getPlan } = usePlan();

	useEffect(() => {
		if (currentWorkspaceId) {
			getPlan(currentWorkspaceId);
		}
	}, [currentWorkspaceId, getPlan]);

	return null;
}

interface LayoutRootProps {
	children: ReactNode;
}

export default function LayoutRoot({ children }: LayoutRootProps) {
	return (
		<PageLoaderProvider>
			<ToastProvider>
				<TooltipProvider>
					<PageLoaderOverlay />
					<FeaturebaseWidget />
					<PlanLoader />
					{children}
				</TooltipProvider>
			</ToastProvider>
		</PageLoaderProvider>
	);
}
